import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { getToken, logout, canAccessAdmin, refreshRolePermissions } from "../services/authService";
import { getCount, getCart } from "../services/cartService";
import { getBasicCategories } from "../services/categoryService";
import { getBasicPublicPages } from "../services/pageService";
import wishlistService from '../services/wishlistService';
import { useCurrency } from '../context/CurrencyContext';
import API_BASE_URL from "../config";
import { resolveAssetUrl } from '../utils/resolveUrl';
import logo from '../assets/Navbar/globe-logo.svg';
// import globeRotate from '../assets/Navbar/globe-rotate.webp';
import Breadcrumbs from './Breadcrumbs';

const NAV_LINKS = [
  // { to: '/sherbimet', label: 'Sherbimet', order: 10 },
  // { to: '/financim', label: 'Financim', order: 20 },
  // { to: '/shkarko-aplikacionin', label: 'Shkarko aplikacionin', order: 30 }

]; const CurrencyIcons = {
  ALL: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 4v16h8" />
    </svg>
  ),
  EUR: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 10h12" />
      <path d="M4 14h9" />
      <path d="M19 6a7.7 7.7 0 0 0-5.2-2A7.9 7.9 0 0 0 6 12c0 4.4 3.5 8 7.8 8 2 0 3.8-.8 5.2-2" />
    </svg>
  ),
  USD: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="2" x2="12" y2="22" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  )
};

export default function Header() {
  const navigate = useNavigate();
  const [showHamburger, setShowHamburger] = useState(false);
  const [panelActive, setPanelActive] = useState(false);
  const [selectedHamburgerCategory, setSelectedHamburgerCategory] = useState(null);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const { currency, changeCurrency } = useCurrency();
  const [hasToken, setHasToken] = useState(!!getToken());
  const [isAdministrator, setIsAdministrator] = useState(canAccessAdmin());
  const [cartCount, setCartCount] = useState(getCount());
  const [wishlistCount, setWishlistCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [headerCategories, setHeaderCategories] = useState([]);
  const [headerPages, setHeaderPages] = useState([]);
  const [headerDataLoaded, setHeaderDataLoaded] = useState(false);
  const [headerDataLoading, setHeaderDataLoading] = useState(false);
  const [hamburgerContentReady, setHamburgerContentReady] = useState(false);
  const [, setShowCategoryMenu] = useState(false);
  const [suggestions, setSuggestions] = useState({ products: [], categories: [] });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const menuRef = useRef(null);
  const hamburgerRef = useRef(null);
  const profileRef = useRef(null);
  const langRef = useRef(null);
  const mobileLangRef = useRef(null);
  const suggestionsRef = useRef(null);
  const headerBarRef = useRef(null);
  const headerDataPromiseRef = useRef(null);
  const mountedRef = useRef(true);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const isMobileViewport = windowWidth <= 768;
  const [mobileHeaderBarHeight, setMobileHeaderBarHeight] = useState(0);
  const location = useLocation();
  const b2bEnabled = location && location.pathname === '/b2b';
  const isSingleProductPage = (() => {
    const parts = (location?.pathname || '').split('/').filter(Boolean);
    return parts[0] === 'products' && parts.length === 2;
  })();
  const lastSubmittedQueryRef = useRef('');

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  const loadHeaderData = useCallback(async () => {
    if (headerDataPromiseRef.current) return headerDataPromiseRef.current;
    setHeaderDataLoading(true);
    // Use basic light versions
    headerDataPromiseRef.current = Promise.all([getBasicCategories(), getBasicPublicPages()])
      .then(([cats, pages]) => {
        if (!mountedRef.current) return;
        setHeaderCategories(Array.isArray(cats) ? cats : []);
        setHeaderPages(Array.isArray(pages) ? pages : []);
        setHeaderDataLoaded(true);
      })
      .catch(err => {
        console.error('Failed to load header data', err);
        if (!mountedRef.current) return;
        setHeaderCategories([]);
        setHeaderPages([]);
      })
      .finally(() => {
        if (mountedRef.current) setHeaderDataLoading(false);
        headerDataPromiseRef.current = null;
      });
    return headerDataPromiseRef.current;
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isMobileViewport) {
      setMobileHeaderBarHeight(0);
      return;
    }

    const update = () => {
      const next = headerBarRef.current ? headerBarRef.current.offsetHeight : 0;
      setMobileHeaderBarHeight(next);
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [isMobileViewport, windowWidth]);

  useEffect(() => {
    const onAnyStorage = async () => {
      const token = getToken();
      setHasToken(!!token);
      try { await refreshRolePermissions(); } catch (_) { }
      setIsAdministrator(canAccessAdmin());
      if (!token) {
        setCartCount(getCount());
      }
    };
    window.addEventListener("storage", onAnyStorage);
    const onCartUpdated = (e) => {
      try {
        const items = (e && e.detail && e.detail.items) || [];
        const count = items.reduce((s, it) => s + (it.qty || 0), 0);
        setCartCount(count);
      } catch (_) { /**/ }
    };
    window.addEventListener('cartUpdated', onCartUpdated);
    // initialize once
    onAnyStorage();
    return () => {
      window.removeEventListener("storage", onAnyStorage);
      window.removeEventListener('cartUpdated', onCartUpdated);
    };
  }, []);

  // refresh when auth state changes in same tab (e.g., after login)
  useEffect(() => {
    let mounted = true;
    async function load() {
      if (hasToken) {
        try {
          const items = await getCart();
          if (!mounted) return;
          const count = items.reduce((s, it) => s + (it.qty || 0), 0);
          setCartCount(count);
        } catch (err) {
          if (!mounted) return;
          setCartCount(0);
        }
      } else {
        setCartCount(getCount());
      }
    }
    load();
    return () => { mounted = false; };
  }, [hasToken]);

  // load wishlist count and listen for updates
  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const list = await wishlistService.getWishlist();
        if (!mounted) return;
        setWishlistCount(Array.isArray(list) ? list.length : 0);
      } catch (err) {
        if (mounted) setWishlistCount(0);
      }
    }
    load();
    const onUpdate = (e) => {
      const items = e && e.detail && e.detail.items ? e.detail.items : [];
      setWishlistCount(Array.isArray(items) ? items.length : 0);
    };
    window.addEventListener('wishlistUpdated', onUpdate);
    return () => { mounted = false; window.removeEventListener('wishlistUpdated', onUpdate); };
  }, []);

  // Re-evaluate auth status on route changes (e.g., after login navigation)
  useEffect(() => {
    setHasToken(!!getToken());
    setIsAdministrator(canAccessAdmin());
  }, [location]);

  // load header data lazily (idle) so first interaction isn't blocked
  // Load header data immediately
  useEffect(() => {
    loadHeaderData();
  }, [loadHeaderData]);

  // when menu opens on desktop, auto-select first category once available
  useEffect(() => {
    if (!showHamburger) return;
    if (windowWidth > 768 && !selectedHamburgerCategory && headerCategories.length) {
      setSelectedHamburgerCategory(headerCategories[0]);
    }
  }, [showHamburger, windowWidth, selectedHamburgerCategory, headerCategories]);

  // close menu on outside click
  useEffect(() => {
    function onDoc(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowCategoryMenu(false);
      }
      if (hamburgerRef.current && !hamburgerRef.current.contains(e.target)) {
        setShowHamburger(false);
      }
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
      if (langRef.current && !langRef.current.contains(e.target) && (!mobileLangRef.current || !mobileLangRef.current.contains(e.target))) {
        setShowLangMenu(false);
      }
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // trigger panel activation on open so CSS transition runs (slide-in)
  useEffect(() => {
    if (showHamburger) {
      // ensure initial render is inactive, then activate on next frame
      setPanelActive(false);
      requestAnimationFrame(() => requestAnimationFrame(() => setPanelActive(true)));
    } else {
      setPanelActive(false);
    }
  }, [showHamburger]);

  // Defer heavy menu content rendering to avoid blocking initial menu open
  useEffect(() => {
    let rafId = null;
    let timerId = null;
    if (showHamburger) {
      setHamburgerContentReady(false);
      rafId = requestAnimationFrame(() => {
        timerId = setTimeout(() => setHamburgerContentReady(true), 0);
      });
    } else {
      setHamburgerContentReady(false);
    }
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (timerId) clearTimeout(timerId);
    };
  }, [showHamburger]);

  //  - page doesnt scroll down when hamburger menu is open 

  // Add this effect to manage body scroll
  useEffect(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

    // Desktop behavior: simple overflow toggle
    if (!isMobile) {
      document.body.style.overflow = showHamburger ? 'hidden' : 'unset';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }

    // Mobile behavior: fully lock the page to prevent the background from scrolling (iOS-safe).
    const html = document.documentElement;
    const prev = {
      bodyOverflow: document.body.style.overflow,
      bodyPosition: document.body.style.position,
      bodyTop: document.body.style.top,
      bodyLeft: document.body.style.left,
      bodyRight: document.body.style.right,
      bodyWidth: document.body.style.width,
      htmlOverflow: html.style.overflow,
    };

    const scrollY = window.scrollY || window.pageYOffset || 0;

    if (showHamburger) {
      html.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    } else {
      html.style.overflow = prev.htmlOverflow;
      document.body.style.overflow = prev.bodyOverflow;
      document.body.style.position = prev.bodyPosition;
      document.body.style.top = prev.bodyTop;
      document.body.style.left = prev.bodyLeft;
      document.body.style.right = prev.bodyRight;
      document.body.style.width = prev.bodyWidth;

      const top = prev.bodyTop || '0px';
      const y = Math.abs(parseInt(top, 10) || 0);
      if (y) window.scrollTo(0, y);
    }

    return () => {
      html.style.overflow = prev.htmlOverflow;
      document.body.style.overflow = prev.bodyOverflow;
      document.body.style.position = prev.bodyPosition;
      document.body.style.top = prev.bodyTop;
      document.body.style.left = prev.bodyLeft;
      document.body.style.right = prev.bodyRight;
      document.body.style.width = prev.bodyWidth;
    };
  }, [showHamburger]);




  // Merge static NAV_LINKS and dynamic headerPages, then sort
  const allNavLinks = React.useMemo(() => {
    const dynamic = headerPages
      .filter(p => p.visible && p.showHeader)
      .map(p => ({
        to: `/${p.slug}`,
        label: p.title,
        order: Number(p.order) || 0
      }));

    const staticLinks = NAV_LINKS.filter(nl => {
      const isShadowed = dynamic.some(d =>
        d.to.toLowerCase().includes(nl.to.toLowerCase()) ||
        d.label.toLowerCase() === nl.label.toLowerCase()
      );
      return !isShadowed;
    }).map(l => ({ ...l }));

    return [...staticLinks, ...dynamic].sort((a, b) => {
      const ordA = Number(a.order) || 0;
      const ordB = Number(b.order) || 0;
      if (ordA !== ordB) return ordA - ordB;
      return a.label.localeCompare(b.label);
    });
  }, [headerPages]);

  // Debounce search suggestions
  useEffect(() => {
    const timer = setTimeout(async () => {
      // Don't show suggestions if empty, too short, or if it exactly matches what we just submitted
      if (!searchQuery || searchQuery.length < 2 || searchQuery === lastSubmittedQueryRef.current) {
        setSuggestions({ products: [], categories: [] });
        setShowSuggestions(false);
        return;
      }
      try {
        const res = await fetch(`${API_BASE_URL}/products/search/suggestions?q=${encodeURIComponent(searchQuery)}`);
        const data = await res.json();
        if (data && (data.products?.length || data.categories?.length)) {
          setSuggestions(data);
          setShowSuggestions(true);
        } else {
          setShowSuggestions(false);
        }
      } catch (err) {
        console.error('Suggestion fetch failed', err);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close suggestions unconditionally when navigating away or submitting search
  useEffect(() => {
    setShowSuggestions(false);
  }, [location.pathname, location.search]);

  // flatten hierarchical categories into a flat list of {id,name}
  /* eslint-disable-next-line no-unused-vars */
  const flattenCategories = (nodes) => {
    const out = [];
    function walk(list) {
      (list || []).forEach(n => {
        if (!n) return;
        out.push({ id: n.id, name: n.name });
        if (Array.isArray(n.subcategories) && n.subcategories.length) walk(n.subcategories);
      });
    }
    walk(nodes);
    return out;
  };
  // intentionally not storing flattened categories (unused in current markup)

  const handleLogout = () => {
    logout();
    setHasToken(false);
    setIsAdministrator(false);
    navigate("/login");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    lastSubmittedQueryRef.current = searchQuery;
    setShowSuggestions(false);
    const params = new URLSearchParams();
    // Reset filters: Only send 'q', effectively searching all categories.
    // If we wanted to search within category, we'd append it here, but user asked for reset.
    if (searchQuery) params.set('q', searchQuery);
    const qs = params.toString();
    navigate(`/products${qs ? `?${qs}` : ''}`);
  };

  // Partition subcategories into balanced columns using the painter's partition DP.
  // Finds the optimal sequential split into numCols parts that minimises the maximum
  // column height (= sum of group heights in that column).
  // numCols is chosen so that total height / 12 ≈ 1 column per 12 visual items.
  function partitionCategories(groups) {
    if (!groups || !groups.length) return [];
    const heights = groups.map(g => 1 + (Array.isArray(g.subcategories) ? g.subcategories.length : 0));
    const totalHeight = heights.reduce((a, b) => a + b, 0);
    const numCols = Math.max(Math.min(groups.length, 4), Math.round(totalHeight / 12));
    const n = groups.length;
    if (numCols >= n) return groups.map(g => [g]);
    if (numCols === 1) return [groups.slice()];
    // Prefix sums
    const prefix = [0];
    heights.forEach(h => prefix.push(prefix[prefix.length - 1] + h));
    // dp[i][j] = min possible max-column-height distributing first i groups into j columns
    const INF = 1e9;
    const dp = Array.from({ length: n + 1 }, () => new Array(numCols + 1).fill(INF));
    const cut = Array.from({ length: n + 1 }, () => new Array(numCols + 1).fill(0));
    dp[0][0] = 0;
    for (let j = 1; j <= numCols; j++) {
      for (let i = j; i <= n; i++) {
        for (let k = j - 1; k < i; k++) {
          const colH = prefix[i] - prefix[k];
          const cost = Math.max(dp[k][j - 1], colH);
          if (cost < dp[i][j]) { dp[i][j] = cost; cut[i][j] = k; }
        }
      }
    }
    // Reconstruct column groupings
    const columns = [];
    let i = n, j = numCols;
    while (j > 0) { const k = cut[i][j]; columns.unshift(groups.slice(k, i)); i = k; j--; }
    return columns;
  }

  // Mega menu renderer: show columns of root categories and nested subcategories
  function CategoryMega({ nodes = [], onSelect }) {
    if (!nodes || !nodes.length) return <div style={{ color: '#666' }}>No categories</div>;
    // split roots into columns (3 columns max)
    const cols = 3;
    const perCol = Math.ceil(nodes.length / cols);
    const columns = [];
    for (let i = 0; i < cols; i++) columns.push(nodes.slice(i * perCol, (i + 1) * perCol));

    return (
      <div style={{ display: 'flex', gap: 24 }}>
        {columns.map((col, ci) => (
          <div key={ci} style={{ minWidth: 180 }}>
            {col.map(cat => (
              <div key={cat.id || cat.name} style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>
                  <button type="button" onClick={() => { if (onSelect) onSelect(cat); }} style={{ color: '#111', textDecoration: 'none', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 'inherit', fontWeight: 'inherit' }}>{cat.name}</button>
                </div>
                {Array.isArray(cat.subcategories) && cat.subcategories.length ? (
                  <ul style={{ listStyle: 'none', paddingLeft: 8, margin: 0 }}>
                    {cat.subcategories.map(sub => (
                      <li key={sub.id || sub.name} style={{ marginBottom: 6 }}>
                        <button type="button" onClick={() => { if (onSelect) onSelect(sub); }} style={{ textDecoration: 'none', color: '#444', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>{sub.name}</button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }



  // Small recursive tree component for dropdown categories
  function CategoryTree({ nodes = [], depth = 0, onSelect }) {
    if (!nodes || !nodes.length) return <div style={{ color: '#666' }}>No categories</div>;
    return (
      <ul style={{ listStyle: 'none', margin: 0, paddingLeft: depth ? 12 : 6 }}>
        {nodes.map(n => (
          <li key={n.id || n.name} style={{ marginBottom: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button type="button" onClick={() => { if (onSelect) onSelect(n); }} style={{ textDecoration: 'none', color: '#111', fontWeight: 600, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>{n.name}</button>
            </div>
            {Array.isArray(n.subcategories) && n.subcategories.length ? (
              <div style={{ marginTop: 6, marginLeft: 8 }}>
                <CategoryTree nodes={n.subcategories} depth={depth + 1} onSelect={onSelect} />
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <header className="site-header mobile-padding" style={{ display: "flex", flexDirection: 'column', gap: 0 }}>
      {/* inline styles for placeholder and search input border to match image */}
      <style>{`
    .header-search-input::placeholder { color: #007595; opacity: 0.95; }
    .header-search-input { 
      border: 1.5px solid #d6e6f3 !important; 
      outline: none !important;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;
    }
    .header-search-input:hover {
      border-color: #b0d4e8 !important;
    }
    .header-search-input:focus,
    .header-search-input:focus-visible {
      outline: none !important;
      border-color: #007da5 !important;
      box-shadow: 0 0 0 3px rgba(0, 125, 165, 0.12) !important;
    }
    /* header icon styling (SVGs use currentColor) */
  .header-icon { color: #007da5; opacity: 0.95; display: inline-block; transition: transform 140ms ease, opacity 140ms ease, color 160ms ease; }
  /* Modern Mac-Style Header Action Buttons with Light Blue/Orange brand tints */
  .header-action {
    position: relative;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #f0f8fb;
    border: 1.5px solid #d0e7f2;
    color: #007da5;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
    cursor: pointer;
    text-decoration: none;
    transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .header-action:hover {
    background: #007da5;
    border-color: #007da5;
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(0, 125, 165, 0.32);
  }

  .header-action:active {
    transform: scale(0.93);
  }

  /* Wishlist / Orange Variant */
  .header-action.action-orange {
    background: #fef6ec;
    border-color: #fde3c7;
    color: #f7931e;
  }

  .header-action.action-orange:hover {
    background: #f7931e;
    border-color: #f7931e;
    color: #ffffff;
    box-shadow: 0 4px 14px rgba(247, 147, 30, 0.32);
  }

  .header-action-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    background: #007da5;
    color: #ffffff;
    border: 2px solid #ffffff;
    border-radius: 10px;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    font-size: 10px;
    font-weight: 700;
    line-height: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.12);
    pointer-events: none;
  }

  .header-action-badge.cart-badge {
    background: #f7931e;
  }





  // .header-action:hover .header-icon { color: #fff; opacity: 1; transform: scale(1.06); }
  /* top nav: keep color stable and show underline without affecting layout */
  .top-nav-link { color: #111; text-decoration: none; position: relative; padding-bottom: 6px; display: inline-block; letter-spacing: -0.5px; }
  .top-nav-link::after { content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: 2px; background: transparent; transition: background 140ms linear; }
  .top-nav-link:hover::after, .top-nav-link.active::after { background: #007da5; }
  .top-nav-link.active { color: #007da5; }

  /* Top row layout fix for overlap */
  .header-top-row {
    display: flex;
    justify-content: center;
    width: 100%;
    position: relative;
    padding: 8px 16px;
    min-height: 40px;
  }

  /* When screen is small but still desktop, allow the row to wrap or stack */
  @media (max-width: 1200px) and (min-width: 769px) {
    .header-top-row {
      flex-direction: column-reverse;
      align-items: center;
      gap: 8px;
    }
    .header-top-controls {
      position: static !important;
      transform: none !important;
      width: 100%;
      justify-content: flex-end;
    }
    .header-top-nav {
      gap: 40px !important; /* Reduce gap between links on smaller screens */
    }
  }

  /* hamburger overlay and slide-in panel */
  .hamburger-backdrop { position: fixed;top: 12%; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0); z-index: 200; display: flex; align-items: flex-start; justify-content: flex-start; padding-top: 0px; pointer-events: none; transition: background 240ms ease; }
  .hamburger-backdrop.open { background: rgba(0,0,0,0.24); pointer-events: auto; }
  .hamburger-panel { position: relative; width: 100%; max-width: 1440px; scrollbar-color: #2790b2 #f4f4f4; box-shadow: 0 12px 40px rgba(0,0,0,0.12); border-radius: 6px; display: flex; overflow: hidden; background: #fff; transform: translateX(-100%); transition: transform 320ms cubic-bezier(.2,.8,.2,1); }
  .hamburger-panel.open { transform: translateX(0); }
  .glb-menu-close-modal-btn {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: #ffffff;
    border: 1.5px solid #e2e8f0;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 100;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    transition: all 0.28s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .glb-menu-close-modal-btn:hover {
    background: #007da5 !important;
    border-color: #007da5 !important;
    color: #ffffff !important;
    transform: rotate(90deg) scale(1.08);
    box-shadow: 0 6px 18px rgba(0, 125, 165, 0.35);
  }
  .glb-menu-close-modal-btn:active {
    background: #006080 !important;
    border-color: #006080 !important;
    color: #ffffff !important;
    transform: rotate(90deg) scale(0.95);
  }
  .mobile-padding {padding-top:8px}
  .site-header-bar { width: 100%; border-bottom: 1px solid #eee; background: #fff; padding-bottom: 5px; }

  /* Mobile responsiveness: keep hamburger usable and prevent header overflow */
  @media (max-width: 768px) {
    .site-header-bar { position: fixed; top: 0; left: 0; right: 0; z-index: 1000; }
    .mobile-padding {padding-top:0px}
    /* 1. Mobile Visibility Utilities */
    .mobile-only { display: block; }
    .mobile-hidden { display: none !important; }

    /* 2. Main Header 2-Row Layout */
    .header-top-row { display: none !important; }
    
    .header-bottom-inner { 
        display: grid !important;
        grid-template-areas: 
            "logo icons"
            "burger search" !important;
        grid-template-columns: auto 1fr !important;
        padding: 8px 12px !important;
        row-gap: 8px !important;
        column-gap: 12px !important;
        align-items: center !important;
        justify-content: stretch !important;
        position: relative;
    }
    
    /* Row 1: Logo (Left) */
    .header-logo-container {
        grid-area: logo;
        display: flex;
        justify-content: flex-start;
        width: auto !important;
    }
    .header-logo-container img {
      height: 44px !important;
    }

    /* Row 1: Icons (Right) */
    .header-actions-group {
        grid-area: icons;
        width: 100% !important;
        display: flex !important;
        justify-content: flex-end !important;
        margin-bottom: 0px !important;
        align-items: center;
        gap: 8px !important;
    }
    .header-action { width: 36px !important; height: 36px !important; }

    /* Row 2: Hamburger (Left) */
    .header-burger-container {
        grid-area: burger;
        display: flex;
        justify-content: flex-start;
        align-items: center;
    }
    .header-burger-container > button {
         height: 38px !important;
         font-size: 13px !important;
         padding: 0 4px !important; /* Minimized padding for trigger button ONLY */
    }
    .hamburger-label { display: none !important; }
       
    
    /* Row 2: Search Bar (Right) */
    .header-search-form { 
        grid-area: search;
        width: 100% !important; 
        margin: 0 !important;
        max-width: 100% !important;
    }
    /* Ensure the inner flex container of the form fills width */
    .header-search-form > div {
        width: 100% !important;
    }
    .header-search-input {
        height: 38px !important;
        width: 100% !important;
        box-sizing: border-box !important; /* Prevent padding from breaking layout */
    }

    /* 3. Hamburger Menu Scroll Fix (Single Container) */
    .hamburger-backdrop {
        align-items: flex-start !important; /* Align top */
      top: var(--mobile-header-height, 0px) !important;
      height: calc(100vh - var(--mobile-header-height, 0px));
    }

    /* Panel is the MASTER SCROLL container */
    .hamburger-panel { 
        width: 100vw !important; 
        max-width: 100vw !important;
        height: 100% !important;
        max-height: 100% !important;
        border-radius: 0; 
        flex-direction: column;
        overflow-y: auto !important; /* ENABLE SCROLL HERE */
        -webkit-overflow-scrolling: touch;
        background: #f4f4f4 !important;
        padding: 16px !important;
        padding-bottom: 80px !important; /* Safe area for bottom */
    }

    /* Inner modules (Aside/Main) should NOT scroll independently */
    .hamburger-aside, .hamburger-main {
        width: 100% !important;
        border: none !important;
        flex: 0 0 auto !important; /* Do not stretch */
        height: auto !important;   /* Let content dictate height */
        overflow: visible !important;
        min-height: 0 !important;
        background: #f4f4f4 !important;
    }

    /* Aside specific */
    .hamburger-aside {
        border-bottom: 1px solid #eee !important;
        background: #f4f4f4 !important;
    }

    /* Main specific */
    .hamburger-main {
        position: relative !important; /* In flow */
        background: #f4f4f4 !important;
    }

    /* 4. Utilities */
    body.menu-open {
        overflow: hidden !important;
    }
  }
  

  `}</style>

      <div
        ref={headerBarRef}
        className="site-header-bar"
        style={isMobileViewport ? { '--mobile-header-height': `${mobileHeaderBarHeight}px` } : undefined}
      >
        {/* top row: centered nav with full-width right controls */}
        <div className="header-top-row" style={{ display: 'flex', justifyContent: 'center', width: '100%', position: 'relative' }}>
          <div style={{ width: '100%', maxWidth: 1440, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <nav className="header-top-nav" style={{ display: 'flex', gap: 135, position: 'relative', flexWrap: 'wrap', justifyContent: 'center' }}>
              {allNavLinks.map(l => (
                <Link key={l.to} to={l.to} className={'top-nav-link' + (location && (location.pathname === l.to || location.pathname.startsWith(l.to + '/')) ? ' active' : '')}>{l.label}</Link>
              ))}
            </nav>
          </div>

          {/* full-width right controls pinned to the far right of the header */}
          <div ref={langRef} className="header-top-controls" style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: 12, zIndex: 9999 }}>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setShowLangMenu(s => !s)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#f0f8fb',
                  border: '1.5px solid #d0e7f2',
                  padding: '5px 12px',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#007da5',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                  transition: 'all 0.2s ease'
                }}
              >
                {CurrencyIcons[currency]}
                <span>{currency} ▾</span>
              </button>
              {showLangMenu && (
                <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 8, background: '#fff', border: '1px solid #eee', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', padding: 0, zIndex: 10001, minWidth: '120px', borderRadius: '6px', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {['ALL', 'EUR', 'USD'].map(code => (
                      <button
                        key={`desktop-${code}`}
                        type="button"
                        onClick={() => { changeCurrency(code); setShowLangMenu(false); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: currency === code ? '#f0f8fb' : '#fff', border: 'none', borderBottom: '1px solid #f5f5f5', width: '100%', textAlign: 'left', cursor: 'pointer', fontSize: '13px', color: currency === code ? '#007da5' : '#555'
                        }}
                      >
                        {CurrencyIcons[code]}
                        <span style={{ fontWeight: currency === code ? '600' : '400' }}>{code}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {isAdministrator ? (
              <Link to="/admin" style={{ padding: '8px 12px', borderRadius: 6, background: '#007da5', color: '#fff', textDecoration: 'none', fontSize: 14 }}>Admin Console</Link>
            ) : null}
          </div>
        </div>
        {/* bottom row: centered container with hamburger (left), search (center), controls (right) */}
        <div className="" style={{ display: 'flex', justifyContent: 'center', width: '100%' }}  >
          {windowWidth <= 768 ? (
            /* Mobile View: 2-Row Layout */
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '8px 12px' }}>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="header-logo-container">
                  <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }} aria-label="Home" >
                    <img src={logo} alt="GLOBE" className="mr-2 mb-1" style={{ height: 44, display: 'block' }} />
                  </Link>
                </div>
                <div className="header-actions-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div className="mobile-only" style={{ position: 'relative' }} ref={mobileLangRef}>
                    <button
                      className="header-action"
                      type="button"
                      onClick={() => setShowLangMenu(s => !s)}
                      aria-label="Valuta"
                      title="Valuta"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                        <path d="M2 12h20" />
                      </svg>
                    </button>
                    {showLangMenu && (
                      <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 8, background: '#fff', border: '1px solid #eee', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', padding: 12, zIndex: 10001, minWidth: 200, borderRadius: 8 }}>
                        <div style={{ display: 'grid', gap: 10 }}>
                          <label style={{ fontSize: 13, fontWeight: 600, color: '#444' }}>Valuta</label>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {['ALL', 'EUR', 'USD'].map(code => (
                              <button
                                key={`mob-${code}`}
                                onClick={() => { changeCurrency(code); setShowLangMenu(false); }}
                                type="button"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-start',
                                  gap: '8px',
                                  padding: '10px 12px',
                                  border: currency === code ? '1px solid #007da5' : '1px solid #eee',
                                  borderRadius: '6px',
                                  background: currency === code ? '#f0f9ff' : '#fff',
                                  color: currency === code ? '#007da5' : '#444',
                                  cursor: 'pointer',
                                  fontWeight: currency === code ? '600' : '400',
                                  transition: 'all 0.2s',
                                  width: '100%',
                                  textAlign: 'left'
                                }}
                              >
                                {CurrencyIcons[code]}
                                <span style={{ fontSize: '14px' }}>{code}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div ref={profileRef} style={{ position: 'relative' }}>
                    <button
                      className="header-action"
                      type="button"
                      onClick={() => { if (!hasToken) navigate('/login'); else setShowProfileMenu(s => !s); }}
                      title="Profili"
                      aria-label="Profili"
                      aria-haspopup="true"
                      aria-expanded={showProfileMenu}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="8" r="4.5" />
                        <path d="M20 21a8 8 0 0 0-16 0" />
                      </svg>
                    </button>
                    {showProfileMenu && hasToken ? (
                      <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 8, background: '#fff', border: '1px solid #eee', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', padding: 8, zIndex: 95, minWidth: 160, borderRadius: 8 }}>
                        <Link to="/profile" onClick={() => setShowProfileMenu(false)} style={{ display: 'block', padding: '8px 12px', textDecoration: 'none', color: '#111', fontWeight: 600, fontSize: 14 }}>Profili</Link>
                        <button type="button" onClick={() => { setShowProfileMenu(false); handleLogout(); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontWeight: 600, fontSize: 14 }}>Çkyçu</button>
                      </div>
                    ) : null}
                  </div>
                  <Link to="/wishlist" className="header-action action-orange" aria-label="Të preferuarat" title="Të preferuarat">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                    </svg>
                    {wishlistCount ? (
                      <span className="header-action-badge">{wishlistCount}</span>
                    ) : null}
                  </Link>
                  <Link to="/cart" className="header-action" aria-label="Koshi" title="Koshi">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                      <path d="M3 6h18" />
                      <path d="M16 10a4 4 0 0 1-8 0" />
                    </svg>
                    {cartCount ? (
                      <span className="header-action-badge cart-badge">{cartCount}</span>
                    ) : null}
                  </Link>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, gap: 12 }}>
                <div className="header-burger-container" ref={hamburgerRef} style={{ position: 'relative' }} >
                  <button className="hover:text-orange-400" type="button" onClick={() => {
                    setShowHamburger(s => {
                      const next = !s;
                      const isMobileView = windowWidth <= 768;
                      if (next) {
                        if (!headerDataLoaded) loadHeaderData();
                        setSelectedHamburgerCategory(!isMobileView && headerCategories && headerCategories.length ? headerCategories[0] : null);
                      }
                      return next;
                    });
                  }} aria-haspopup="dialog" aria-expanded={showHamburger} aria-controls="hamburger-menu" style={{ background: 'none', border: '1.5px solid #a5a5a5', padding: '0 8px', cursor: 'pointer', fontSize: 10, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 40, width: 45 }}>
                    <span className="text-3xl !text-cyan-600 ">☰</span>
                  </button>
                  {showHamburger ? (
                    <div aria-hidden={!showHamburger} className={'hamburger-backdrop' + (panelActive ? ' open' : '')} onClick={() => setShowHamburger(false)} >
                      <div id="hamburger-menu" role="dialog" aria-modal="true" className={'hamburger-panel p-10 !h-full  !bg-[#f4f4f4]' + (panelActive ? ' open' : '')} onClick={(e) => e.stopPropagation()} >
                        {/* Close button top-right (desktop only) */}
                        <button
                          onClick={() => setShowHamburger(false)}
                          className="glb-menu-close-modal-btn desktop-only"
                          style={{
                            display: windowWidth <= 768 ? 'none' : 'flex'
                          }}
                          aria-label="Mbyll menunë"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                        {/* left column: categories list */}
                        <aside style={{ width: 420, borderRight: '1px solid #eee', padding: windowWidth <= 768 ? '10px 12px' : 18, background: '#f4f4f4' }} className={`aside-menu hamburger-aside ${selectedHamburgerCategory && windowWidth <= 768 ? 'mobile-hidden' : ''}`}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }} >
                            <strong className="text-gray-900" style={{ fontSize: windowWidth <= 768 ? 22 : 26 }}>Të gjitha Kategoritë</strong>
                            {windowWidth <= 768 && (
                              <button
                                onClick={() => setShowHamburger(false)}
                                style={{
                                  height: 36,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 5,
                                  background: '#ffffff',
                                  border: '1.5px solid #e2e8f0',
                                  borderRadius: 10,
                                  paddingLeft: 16,
                                  paddingRight: 24,
                                  fontSize: 13,
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  color: '#64748b',
                                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.04)',
                                  transition: 'all 0.2s ease',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                  <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                                <span>Mbyll</span>
                              </button>
                            )}
                          </div>
                          <nav style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {!hamburgerContentReady ? (
                              <div style={{ color: '#666' }}>Loading menu…</div>
                            ) : headerDataLoading && !headerCategories.length ? (
                              <div style={{ color: '#666' }}>Loading categories...</div>
                            ) : headerCategories && headerCategories.length ? headerCategories.map(cat => (
                              <button key={cat.id || cat.name} onClick={() => setSelectedHamburgerCategory(cat)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 12px', borderRadius: 6, background: selectedHamburgerCategory && (selectedHamburgerCategory.id === cat.id) ? '#f0f0f0ff' : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                  {cat.image ? (
                                    <img loading="lazy" src={resolveAssetUrl(cat.image)} alt={cat.name} style={{ width: '30px', height: '30px' }} />
                                  ) : (
                                    <div style={{ width: '30px', height: '30px', background: '#fafafa' }} />
                                  )}
                                  <div className="hover:underline" style={{ fontWeight: 700, color: '#1a1a1a' }}>{cat.name}</div>
                                </div>
                                <div className="text-2xl" style={{ color: '#333' }}>›</div>
                              </button>
                            )) : (
                              <div style={{ color: '#666' }}>No categories</div>
                            )}
                          </nav>

                          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #ddd' }}>
                            <nav style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                              {allNavLinks.map(l => (
                                <Link
                                  key={l.to}
                                  to={l.to}
                                  onClick={() => setShowHamburger(false)}
                                  style={{
                                    textDecoration: 'none',
                                    color: '#333',
                                    fontSize: 16,
                                    fontWeight: 600,
                                    padding: '8px 12px',
                                    borderRadius: 6,
                                    background: location && (location.pathname === l.to || location.pathname.startsWith(l.to + '/')) ? '#e0f2fe' : 'transparent'
                                  }}
                                >
                                  {l.label}
                                </Link>
                              ))}
                            </nav>
                          </div>
                        </aside>

                        {/* right column: mega content for selected category */}
                        <main className={`!overflow-auto hamburger-main ${!selectedHamburgerCategory && windowWidth <= 768 ? 'mobile-hidden' : ''}`} style={{ flex: 1, padding: windowWidth <= 768 ? '10px 12px 20px 12px' : 20, background: '#f4f4f4' }}>
                          {!hamburgerContentReady ? (
                            <div style={{ color: '#666' }}>Loading menu…</div>
                          ) : headerDataLoading && !headerCategories.length ? (
                            <div style={{ color: '#666' }}>Loading menu…</div>
                          ) : selectedHamburgerCategory ? (
                            <div>
                              {/* Mobile Dual Action Bar: 50% Mbrapa te Kategoritë | 50% Mbyll Menunë */}
                              {windowWidth <= 768 && (
                                <div className="mobile-only" style={{ marginTop: 0, marginBottom: 14, display: 'flex', gap: 8, width: '100%' }}>
                                  <button
                                    onClick={() => setSelectedHamburgerCategory(null)}
                                    style={{
                                      flex: 1,
                                      height: 38,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: 6,
                                      background: '#ffffff',
                                      border: '1.5px solid rgba(0, 125, 165, 0.35)',
                                      borderRadius: 10,
                                      padding: '0 6px',
                                      fontSize: 12.5,
                                      fontWeight: 700,
                                      cursor: 'pointer',
                                      color: '#007da5',
                                      boxShadow: '0 2px 6px rgba(0, 125, 165, 0.08)',
                                      transition: 'all 0.2s ease',
                                      whiteSpace: 'nowrap',
                                      textAlign: 'center'
                                    }}
                                  >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                      <path d="M19 12H5M11 18l-6-6 6-6" />
                                    </svg>
                                    <span>Mbrapa te Kategoritë</span>
                                  </button>

                                  <button
                                    onClick={() => setShowHamburger(false)}
                                    style={{
                                      flex: 1,
                                      height: 38,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: 6,
                                      background: '#ffffff',
                                      border: '1.5px solid #e2e8f0',
                                      borderRadius: 10,
                                      padding: '0 6px',
                                      fontSize: 12.5,
                                      fontWeight: 700,
                                      cursor: 'pointer',
                                      color: '#64748b',
                                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.04)',
                                      transition: 'all 0.2s ease',
                                      whiteSpace: 'nowrap',
                                      textAlign: 'center'
                                    }}
                                  >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                      <path d="M18 6L6 18M6 6l12 12" />
                                    </svg>
                                    <span>Mbyll Menunë</span>
                                  </button>
                                </div>
                              )}

                              <h3 className="text-2xl font-semibold" style={{ marginTop: 0, marginBottom: 18, fontWeight: 600 }}>{selectedHamburgerCategory.name}</h3>
                              {Array.isArray(selectedHamburgerCategory.subcategories) && selectedHamburgerCategory.subcategories.length ? (
                                <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                  {partitionCategories(selectedHamburgerCategory.subcategories).map((col, ci) => (
                                    <div key={ci} style={{ minWidth: 160, flex: '0 0 auto' }}>
                                      {col.map((sc) => (
                                        <div key={sc.id || sc.name} style={{ marginBottom: 18 }}>
                                          <div style={{ fontWeight: 600, marginBottom: 6 }}>
                                            <button className="" type="button" onClick={() => { setShowHamburger(false); if (sc && sc.id) navigate(`/products?categoryId=${encodeURIComponent(sc.id)}`); else navigate(`/products?category=${encodeURIComponent(sc.name)}`); }} style={{ color: '#111', textDecoration: 'none', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 'inherit', fontWeight: 600 }}>{sc.name}</button>
                                          </div>
                                          {Array.isArray(sc.subcategories) && sc.subcategories.length ? (
                                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                              {sc.subcategories.map(ss => (
                                                <li key={ss.id || ss.name} style={{ marginBottom: 6 }}>
                                                  <button className="categoryBtn text-start hover:underline" type="button" onClick={() => { setShowHamburger(false); if (ss && ss.id) navigate(`/products?categoryId=${encodeURIComponent(ss.id)}`); else navigate(`/products?category=${encodeURIComponent(ss.name)}`); }} style={{ background: 'none', border: 'none', padding: 0, color: '#313131ff', cursor: 'pointer' }}>{ss.name}</button>
                                                </li>
                                              ))}
                                            </ul>
                                          ) : null}
                                        </div>
                                      ))}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div style={{ color: '#666' }}>No subcategories</div>
                              )}
                            </div>
                          ) : (
                            <div>
                              <h3 style={{ marginTop: 0 }}>All categories</h3>
                              {headerCategories && headerCategories.length ? (
                                <CategoryMega nodes={headerCategories} onSelect={(node) => { setShowHamburger(false); if (node && node.id) navigate(`/products?categoryId=${encodeURIComponent(node.id)}`); else if (node && node.name) navigate(`/products?category=${encodeURIComponent(node.name)}`); }} />
                              ) : (
                                <div style={{ color: '#666' }}>No categories</div>
                              )}
                            </div>
                          )}
                        </main>
                      </div>
                    </div>
                  ) : null}
                </div>

                <form className="header-search-form" action="/products" onSubmit={handleSearch} style={{ display: 'flex', flex: 1 }}>
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: 1 }}>
                    <input
                      type="search"
                      name="q"
                      className="header-search-input"
                      placeholder="Çfarë produkti po kërkoni?"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{ padding: '0 40px 0 12px', fontSize: 16, borderRadius: 6, flex: 1, height: 40, width: '100%', WebkitAppearance: 'none' }}
                    />
                    <button type="submit" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#007da5' }} aria-label="Search">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                    </button>
                    {showSuggestions && (suggestions.products.length > 0 || suggestions.categories.length > 0) && (
                      <div ref={suggestionsRef} style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #d6e6f3', borderTop: 'none', borderRadius: '0 0 6px 6px', boxShadow: '0 12px 32px rgba(0,0,0,0.1)', zIndex: 200, overflow: 'hidden', marginTop: 4 }}>
                        {suggestions.categories.length > 0 && (
                          <div style={{ padding: '6px 0', borderBottom: '1px solid #eaeff5' }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#99aab5', padding: '4px 14px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Kategoritë</div>
                            {suggestions.categories.map(cat => (
                              <button key={cat.id || cat.name} type="button" className="suggestion-item" onClick={() => { navigate(`/products?category=${encodeURIComponent(cat.name)}`); setShowSuggestions(false); setSearchQuery(''); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#111', fontWeight: 500 }}>{cat.name}</button>
                            ))}
                          </div>
                        )}
                        {suggestions.products.length > 0 && (
                          <div style={{ padding: '6px 0' }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#99aab5', padding: '4px 14px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Produkte</div>
                            {suggestions.products.map(prod => (
                              <button key={prod.id} type="button" className="suggestion-item" onClick={() => { navigate(`/products/${prod.id}`); setShowSuggestions(false); setSearchQuery(''); }} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer' }}>
                                <img src={prod.images && prod.images[0] ? (prod.images[0].startsWith('http') ? prod.images[0] : `${API_BASE_URL.replace('/api', '')}${prod.images[0]}`) : 'https://via.placeholder.com/40'} alt="" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 4, background: '#fff', border: '1px solid #eee' }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 12, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>{prod.name}</div>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: '#007da5', marginTop: 1 }}>{prod.offerPrice ? (<span><span style={{ textDecoration: 'line-through', color: '#999', marginRight: 6, fontWeight: 400, fontSize: 11 }}>{prod.price} L</span>{prod.offerPrice} L</span>) : (<span>{prod.price} L</span>)}</div>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          ) : (
            /* Desktop View: Original Layout */
            <div className="header-bottom-inner" style={{ width: '100%', maxWidth: 1480, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}  >

              {/* Group 2: Logo */}
              <div className="header-logo-container">
                <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }} aria-label="Home" >
                  <img src={logo} alt="GLOBE" className="mr-2 mb-1" style={{ height: 32, display: 'block' }} />
                </Link>
              </div>


              {/* Group 1: Hamburger */}
              <div className="header-burger-container" ref={hamburgerRef} style={{ position: 'relative' }} >
                <button className="hover:text-orange-400" type="button" onClick={() => {
                  setShowHamburger(s => {
                    const next = !s;
                    if (next) {
                      if (!headerDataLoaded) loadHeaderData();
                      setSelectedHamburgerCategory(headerCategories && headerCategories.length ? headerCategories[0] : null);
                    }
                    return next;
                  });
                }} aria-haspopup="dialog" aria-expanded={showHamburger} aria-controls="hamburger-menu" style={{ background: 'none', border: '1.5px solid #a5a5a5', padding: '0 12px', cursor: 'pointer', fontSize: 10, borderRadius: 6, display: 'inline-flex', alignItems: 'center', gap: 8, height: 45 }}><span className="text-3xl px-2 !text-cyan-600 ">☰</span><span className="hamburger-label" style={{ fontSize: 16 }}>Menu</span></button>
                {showHamburger ? (
                  <div aria-hidden={!showHamburger} className={'hamburger-backdrop' + (panelActive ? ' open' : '')} onClick={() => setShowHamburger(false)} >
                    <div id="hamburger-menu" role="dialog" aria-modal="true" className={'hamburger-panel p-10 !h-full  !bg-[#f4f4f4]' + (panelActive ? ' open' : '')} onClick={(e) => e.stopPropagation()} >
                      {/* Close button top-right */}
                      <button
                        onClick={() => setShowHamburger(false)}
                        className="glb-menu-close-modal-btn"
                        aria-label="Mbyll menunë"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>

                      {/* left column: categories list */}
                      <aside style={{ width: 420, borderRight: '1px solid #eee', padding: 18, background: '#f4f4f4' }} className="aside-menu hamburger-aside">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }} >
                          <strong className="text-gray-900" style={{ fontSize: 26 }}>Të gjitha Kategoritë</strong>
                        </div>
                        <nav style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {!hamburgerContentReady ? (
                            <div style={{ color: '#666' }}>Loading menu…</div>
                          ) : headerDataLoading && !headerCategories.length ? (
                            <div style={{ color: '#666' }}>Loading categories...</div>
                          ) : headerCategories && headerCategories.length ? headerCategories.map(cat => (
                            <button key={cat.id || cat.name} onClick={() => setSelectedHamburgerCategory(cat)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 12px', borderRadius: 6, background: selectedHamburgerCategory && (selectedHamburgerCategory.id === cat.id) ? '#f0f0f0ff' : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', textTransform: "uppercase" }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {cat.image ? (
                                  <img loading="lazy" src={resolveAssetUrl(cat.image)} alt={cat.name} style={{ width: '30px', height: '30px' }} />
                                ) : (
                                  <div style={{ width: '30px', height: '30px', background: '#fafafa' }} />
                                )}
                                <div className="hover:underline" style={{ fontWeight: 700, color: '#1a1a1a' }}>{cat.name}</div>
                              </div>
                              <div className="text-2xl" style={{ color: '#333' }}>›</div>
                            </button>
                          )) : (
                            <div style={{ color: '#666' }}>No categories</div>
                          )}
                        </nav>

                        <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #ddd' }}>
                          <nav style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {NAV_LINKS.map(l => (
                              <Link
                                key={l.to}
                                to={l.to}
                                onClick={() => setShowHamburger(false)}
                                style={{
                                  textDecoration: 'none',
                                  color: '#333',
                                  fontSize: 16,
                                  fontWeight: 600,
                                  padding: '8px 12px',
                                  borderRadius: 6,
                                  background: location && (location.pathname === l.to || location.pathname.startsWith(l.to + '/')) ? '#e0f2fe' : 'transparent'
                                }}
                              >
                                {l.label}
                              </Link>
                            ))}
                          </nav>
                        </div>
                      </aside>

                      {/* right column: mega content for selected category */}
                      <main className="!overflow-auto hamburger-main" style={{ flex: 1, padding: 20, background: '#f4f4f4' }}>
                        {!hamburgerContentReady ? (
                          <div style={{ color: '#666' }}>Loading menu…</div>
                        ) : headerDataLoading && !headerCategories.length ? (
                          <div style={{ color: '#666' }}>Loading menu…</div>
                        ) : selectedHamburgerCategory ? (
                          <div>
                            <h3 className="text-2xl font-semibold" style={{ marginTop: 0, marginBottom: 18, fontWeight: 600 }}>{selectedHamburgerCategory.name}</h3>
                            {Array.isArray(selectedHamburgerCategory.subcategories) && selectedHamburgerCategory.subcategories.length ? (
                              <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                {partitionCategories(selectedHamburgerCategory.subcategories).map((col, ci) => (
                                  <div key={ci} style={{ minWidth: 160, flex: '0 0 auto' }}>
                                    {col.map((sc) => (
                                      <div key={sc.id || sc.name} style={{ marginBottom: 18 }}>
                                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                                          <button className="" type="button" onClick={() => { setShowHamburger(false); if (sc && sc.id) navigate(`/products?categoryId=${encodeURIComponent(sc.id)}`); else navigate(`/products?category=${encodeURIComponent(sc.name)}`); }} style={{ color: '#111', textDecoration: 'none', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 'inherit', fontWeight: 600, textTransform: "uppercase" }}>{sc.name}</button>
                                        </div>
                                        {Array.isArray(sc.subcategories) && sc.subcategories.length ? (
                                          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                            {sc.subcategories.map(ss => (
                                              <li key={ss.id || ss.name} style={{ marginBottom: 6 }}>
                                                <button className="text-start hover:underline" type="button" onClick={() => { setShowHamburger(false); if (ss && ss.id) navigate(`/products?categoryId=${encodeURIComponent(ss.id)}`); else navigate(`/products?category=${encodeURIComponent(ss.name)}`); }} style={{ background: 'none', border: 'none', padding: 0, color: '#313131ff', cursor: 'pointer', textTransform: "uppercase" }}>{ss.name}</button>
                                              </li>
                                            ))}
                                          </ul>
                                        ) : null}
                                      </div>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={{ color: '#666' }}>No subcategories</div>
                            )}
                          </div>
                        ) : (
                          <div>
                            <h3 style={{ marginTop: 0 }}>All categories</h3>
                            {headerCategories && headerCategories.length ? (
                              <CategoryMega nodes={headerCategories} onSelect={(node) => { setShowHamburger(false); if (node && node.id) navigate(`/products?categoryId=${encodeURIComponent(node.id)}`); else if (node && node.name) navigate(`/products?category=${encodeURIComponent(node.name)}`); }} />
                            ) : (
                              <div style={{ color: '#666' }}>No categories</div>
                            )}
                          </div>
                        )}
                      </main>

                    </div>
                  </div>
                ) : null}
              </div>



              <form className="header-search-form" action="/products" onSubmit={handleSearch} style={{ display: 'flex', gap: 12, alignItems: 'center', flex: 1 }} >
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: 1 }} >
                  <input
                    type="search"
                    name="q"
                    className="header-search-input"
                    placeholder="Cfarë produkti po kërkoni?"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ padding: '0 12px', fontSize: 16, borderRadius: 6, flex: 1, height: 45, display: 'inline-block', WebkitAppearance: 'none' }}
                  />
                  <button type="submit" style={{ display: 'none' }} aria-hidden="true" tabIndex="-1">Search</button>
                  {showSuggestions && (suggestions.products.length > 0 || suggestions.categories.length > 0) && (
                    <div ref={suggestionsRef} style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #d6e6f3', borderTop: 'none', borderRadius: '0 0 6px 6px', boxShadow: '0 12px 32px rgba(0,0,0,0.1)', zIndex: 200, overflow: 'hidden', marginTop: 4 }}>
                      {suggestions.categories.length > 0 && (
                        <div style={{ padding: '6px 0', borderBottom: '1px solid #eaeff5' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#99aab5', padding: '4px 14px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Categories</div>
                          {suggestions.categories.map(cat => (
                            <button key={cat.id || cat.name} type="button" className="suggestion-item" onClick={() => { navigate(`/products?category=${encodeURIComponent(cat.name)}`); setShowSuggestions(false); setSearchQuery(''); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#111', fontWeight: 500 }}>{cat.name}</button>
                          ))}
                        </div>
                      )}
                      {suggestions.products.length > 0 && (
                        <div style={{ padding: '6px 0' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#99aab5', padding: '4px 14px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Products</div>
                          {suggestions.products.map(prod => (
                            <button key={prod.id} type="button" className="suggestion-item" onClick={() => { navigate(`/products/${prod.id}`); setShowSuggestions(false); setSearchQuery(''); }} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer' }}>
                              <img src={prod.images && prod.images[0] ? (prod.images[0].startsWith('http') ? prod.images[0] : `${API_BASE_URL.replace('/api', '')}${prod.images[0]}`) : 'https://via.placeholder.com/40'} alt="" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 4, background: '#fff', border: '1px solid #eee' }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>{prod.name}</div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#007da5', marginTop: 1 }}>{prod.offerPrice ? (<span><span style={{ textDecoration: 'line-through', color: '#999', marginRight: 6, fontWeight: 400, fontSize: 12 }}>{prod.price} L</span>{prod.offerPrice} L</span>) : (<span>{prod.price} L</span>)}</div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </form>

              {/* Actions Group */}
              <div className="header-actions-group" style={{ display: 'flex', alignItems: 'center' }}>
                <div className="mobile-hidden" style={{ marginLeft: 8, display: 'flex', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => { if (!b2bEnabled) navigate('/b2b'); else navigate('/'); }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      height: 40,
                      boxSizing: 'border-box',
                      padding: '0 14px 0 10px',
                      borderRadius: 14,
                      border: b2bEnabled ? '1.5px solid #f7931e' : '1.5px solid #d0e7f2',
                      background: b2bEnabled ? '#fef6ec' : '#f0f8fb',
                      cursor: 'pointer',
                      boxShadow: b2bEnabled ? '0 1px 4px rgba(247, 147, 30, 0.14)' : '0 1px 3px rgba(0,0,0,0.02)',
                      transition: 'all 0.22s ease'
                    }}
                    aria-pressed={b2bEnabled}
                    title="B2B"
                  >
                    <div style={{ width: 34, height: 20, borderRadius: 20, background: b2bEnabled ? '#f7931e' : '#cbd5e1', position: 'relative', transition: 'background 0.2s ease' }}>
                      <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: (b2bEnabled ? 17 : 3), transition: 'left 0.2s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                    </div>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: b2bEnabled ? '#f7931e' : '#007da5', letterSpacing: '-0.2px' }}>B2B</div>
                  </button>
                </div>
                <div style={{ marginLeft: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div ref={profileRef} style={{ position: 'relative' }}>
                    <button
                      className="header-action"
                      type="button"
                      onClick={() => { if (!hasToken) navigate('/login'); else setShowProfileMenu(s => !s); }}
                      title="Profili"
                      aria-label="Profili"
                      aria-haspopup="true"
                      aria-expanded={showProfileMenu}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="8" r="4.5" />
                        <path d="M20 21a8 8 0 0 0-16 0" />
                      </svg>
                    </button>
                    {showProfileMenu && hasToken ? (
                      <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 8, background: '#fff', border: '1px solid #eee', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', padding: 8, zIndex: 95, minWidth: 160, borderRadius: 8 }}>
                        <Link to="/profile" onClick={() => setShowProfileMenu(false)} style={{ display: 'block', padding: '8px 12px', textDecoration: 'none', color: '#111', fontWeight: 600, fontSize: 14 }}>Profili</Link>
                        <button type="button" onClick={() => { setShowProfileMenu(false); handleLogout(); }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontWeight: 600, fontSize: 14 }}>Çkyçu</button>
                      </div>
                    ) : null}
                  </div>
                  <Link to="/wishlist" className="header-action action-orange" aria-label="Të preferuarat" title="Të preferuarat">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                    </svg>
                    {wishlistCount ? (
                      <span className="header-action-badge">{wishlistCount}</span>
                    ) : null}
                  </Link>
                  <Link to="/cart" className="header-action" aria-label="Koshi" title="Koshi">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                      <path d="M3 6h18" />
                      <path d="M16 10a4 4 0 0 1-8 0" />
                    </svg>
                    {cartCount ? (
                      <span className="header-action-badge cart-badge">{cartCount}</span>
                    ) : null}
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

      </div>

      {isMobileViewport ? <div style={{ height: mobileHeaderBarHeight }} /> : null}

      {/* LOREM - border -  */}
      {/* announcement bar: on homepage show announcement; on other pages show breadcrumbs */}
      {
        location && location.pathname !== '/' && !location.pathname.startsWith('/admin') && (
          <>
            {(!isSingleProductPage || !isMobileViewport) ? (
              <div style={{ width: '100%', background: '#f5f7f9', borderBottom: '1px solid #e8ecf0', padding: '12px 0' }}>
                <div style={{ maxWidth: 1540, margin: '0 auto', padding: '0 18px' }}>
                  <Breadcrumbs />
                </div>
              </div>
            ) : null}

            {(() => {
              const parts = location.pathname.split('/').filter(Boolean);
              const isFirstLevel = parts.length <= 1;

              if (isFirstLevel) return null;

              return (
                <div style={{ width: '100%', margin: '8px 0' }}>
                  <div style={{
                    maxWidth: 1200,
                    margin: '0 auto',
                    padding: '0 16px',
                    display: 'flex',
                    justifyContent: 'flex-start'
                  }}>
                    <button
                      onClick={() => navigate(-1)}
                      className="header-back-button"
                      style={{
                        background: '#007da5',
                        border: 'none',
                        color: '#fff',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '6px 12px',
                        borderRadius: '6px',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 6px rgba(0, 125, 165, 0.2)',
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = '#f7931e';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 10px rgba(247, 147, 30, 0.3)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = '#007da5';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 125, 165, 0.2)';
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                      <span>Mbrapa</span>
                    </button>
                  </div>
                </div>
              );
            })()}
          </>
        )
      }
    </header >
  );
}
